import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str, visibilityCss } from '../_shared/blockRender.js';
import { MaterialIcon, MaterialSymbolsFont } from '../_shared/materialSymbols.js';
/**
 * Shrine `icon-with-text` (preset "Icons with text", category "Icons") — config-by-config. A compact
 * list of up to THREE icon + heading rows in the product information column (e.g. ✓ Money-back, 🚚 Fast
 * shipping, ↩ Easy returns). Each row's icon is a Google Material Symbols NAME (the Shopify model — the
 * merchant types the name; a custom image overrides it), so it shares the exact icon engine as the
 * `icons-with-content` section via {@link MaterialIcon}.
 *
 * SHRINE INVENTORY (27 settings — every one mapped):
 *  - `visibility` (Display on) → shared `visibilityField` + `visibilityCss`.
 *  - `layout` (horizontal / vertical) → icon beside vs above the heading per row.
 *  - `icon_color` (accent-1 / accent-2 / outline-button / text) → `schemeColor`.
 *  - Desktop: `desktop_icon_size` / `desktop_spacing` / `desktop_text_size` (range) → the ≥750px CSS.
 *  - Mobile:  `mobile_icon_size` / `mobile_spacing` / `mobile_text_size` (range) → the base (<750px) CSS.
 *  - OURS (22/09/2026, founder request): `desktopWidth` / `mobileWidth` (range, % of the parent column,
 *    default 100 = unchanged). A narrowed block sits where its layout puts the items — centred under
 *    `horizontal`, flush-start under `vertical` — so the rows never drift away from the text they sit with.
 *  - Content: `icon_{1,2,3}` (Material Symbols name) + `icon_{1,2,3}_fill` (FILL axis) +
 *    `image_{1,2,3}` (custom image override) + `heading_{1,2,3}` — a row renders only when its heading
 *    is non-empty (Shrine `unless heading_N_empty`).
 *  - `margin_top` / `margin_bottom` → shared `marginFields` + `marginStyle`.
 *
 * DEFERRED (Shopify-platform only): `image_picker` is rendered via our `image` field; the Material
 * Symbols glyph FONT is now implemented (was Shrine's only "platform" dependency). `block.shopify_attributes`
 * and the preset `category` are Shopify section-renderer concepts with no authoring control here.
 */
const ICON_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'outline-button', label: 'Outline button' },
    { value: 'text', label: 'Text' },
];
function row(n, defIcon) {
    return [
        {
            id: `icon${n}`,
            type: 'icon-name',
            label: `Icon ${n}`,
            group: 'Content',
            default: defIcon,
            placeholder: defIcon,
        },
        {
            id: `icon${n}Fill`,
            type: 'checkbox',
            label: `Icon ${n} fill`,
            group: 'Content',
            default: false,
        },
        {
            id: `image${n}`,
            type: 'image',
            label: `Image ${n} (overrides the icon)`,
            group: 'Content',
        },
        {
            id: `heading${n}`,
            type: 'text',
            label: `Heading ${n}`,
            group: 'Content',
            default: 'Titre',
            maxLength: 80,
        },
    ];
}
export const iconWithTextBlockSchema = {
    type: 'icon-with-text',
    name: 'Icons with text',
    fields: [
        {
            id: 'layout',
            aiConfigurable: true,
            type: 'select',
            label: 'Layout',
            default: 'horizontal',
            options: [
                { value: 'horizontal', label: 'Horizontal' },
                { value: 'vertical', label: 'Vertical' },
            ],
        },
        {
            id: 'iconColor',
            aiConfigurable: true,
            type: 'select',
            label: 'Icon color',
            default: 'accent-1',
            options: ICON_COLOR_OPTIONS,
        },
        visibilityField,
        {
            id: 'desktopIconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Desktop',
            min: 16,
            max: 72,
            step: 4,
            default: 48,
            unit: 'px',
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Icon & text spacing',
            group: 'Desktop',
            min: 2,
            max: 24,
            step: 2,
            default: 12,
            unit: 'px',
        },
        {
            id: 'desktopTextSize',
            type: 'range',
            label: 'Text size',
            group: 'Desktop',
            min: 12,
            max: 34,
            step: 2,
            default: 18,
            unit: 'px',
        },
        {
            id: 'desktopWidth',
            type: 'range',
            label: 'Width',
            info: 'Share of the column the block takes up. At 100% it fills it, as before.',
            group: 'Desktop',
            min: 20,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
        },
        {
            id: 'mobileIconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Mobile',
            min: 12,
            max: 60,
            step: 4,
            default: 40,
            unit: 'px',
        },
        {
            id: 'mobileSpacing',
            type: 'range',
            label: 'Icon & text spacing',
            group: 'Mobile',
            min: 2,
            max: 24,
            step: 2,
            default: 10,
            unit: 'px',
        },
        {
            id: 'mobileTextSize',
            type: 'range',
            label: 'Text size',
            group: 'Mobile',
            min: 8,
            max: 26,
            step: 2,
            default: 14,
            unit: 'px',
        },
        {
            id: 'mobileWidth',
            type: 'range',
            label: 'Width',
            info: 'Share of the column the block takes up. At 100% it fills it, as before.',
            group: 'Mobile',
            min: 20,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
        },
        ...row(1, 'favorite'),
        ...row(2, 'undo'),
        ...row(3, 'local_shipping'),
        ...marginFields(15, 15),
    ],
};
/**
 * Up to three icon + heading rows. Each row renders only when its heading is non-empty (Shrine parity).
 * Icon/text sizes + the icon↔text gap are responsive at Shrine's 750px breakpoint via BOTH `@media` (live
 * viewport) and `@container aqx-page` (builder device-preview); the icon size is driven through the shared
 * `--aqx-icon-size` var so {@link MaterialIcon} reflows without per-breakpoint markup.
 */
const IconWithTextBlock = ({ block }) => {
    // PRESENTATIONAL block: renders an icon + text from its OWN settings, reads NO product/variant state — so
    // it must render anywhere it is placed (image-with-text, rich-text, …), not only inside a product
    // section's ProductStateProvider. (A stray `useProductState()` guard made it vanish outside one.)
    const bs = block.settings;
    const cls = `aqx-iwt-${block.id}`;
    const layout = str(bs.layout, 'horizontal');
    const horizontal = layout === 'horizontal';
    const iconColor = schemeColor(bs.iconColor ?? 'accent-1');
    const dIcon = num(bs.desktopIconSize, 48);
    const dGap = num(bs.desktopSpacing, 12);
    const dText = num(bs.desktopTextSize, 18);
    const mIcon = num(bs.mobileIconSize, 40);
    const mGap = num(bs.mobileSpacing, 10);
    const mText = num(bs.mobileTextSize, 14);
    const dWidth = num(bs.desktopWidth, 100);
    const mWidth = num(bs.mobileWidth, 100);
    const items = [1, 2, 3]
        .map((n) => ({
        n,
        icon: str(bs[`icon${n}`], ''),
        filled: bs[`icon${n}Fill`] === true,
        image: bs[`image${n}`],
        heading: str(bs[`heading${n}`], ''),
    }))
        .filter((it) => it.heading.trim().length > 0);
    if (items.length === 0)
        return null;
    // Shrine `layout`: HORIZONTAL → the items sit in a centered row, each a column (icon ABOVE centered
    // text); VERTICAL → the items stack, each a row (icon BESIDE the text). Matches Shrine's
    // .icon-with-text--horizontal / --vertical rules.
    const ulLayout = horizontal
        ? 'flex-direction:row;justify-content:center;column-gap:24px;flex-wrap:wrap'
        : 'flex-direction:column;gap:var(--aqx-theme-space-3, 12px)';
    const itemLayout = horizontal
        ? 'flex-direction:column;text-align:center;flex:1 1 0;min-inline-size:0;'
        : '';
    // Width, per device (% of the parent column). 100 (or unset) emits NOTHING so an untouched block is
    // byte-identical. Below 100 the <ul> becomes a narrower box positioned by its layout: centred when the
    // items sit in a row, flush-start (RTL-aware) when they stack. When only the phone is narrowed, the
    // desktop rule has to hand the width back, or the phone value would leak into the wide screen.
    const boxInline = horizontal ? 'auto' : '0 auto';
    const narrow = (w) => w > 0 && w < 100 ? `width:${w}%;margin-inline:${boxInline}` : '';
    const mobileWidthCss = narrow(mWidth);
    const desktopWidthCss = narrow(dWidth) || (mobileWidthCss ? 'width:auto;margin-inline:0' : '');
    const desktopRule = `.${cls}__item{--aqx-icon-size:${dIcon}px;gap:${dGap}px}.${cls}__text{font-size:${dText}px}` +
        (desktopWidthCss ? `.${cls}{${desktopWidthCss}}` : '');
    const css = `.${cls}{list-style:none;margin:0;padding:0;display:flex;${ulLayout};font-family:var(--aqx-theme-font)` +
        (mobileWidthCss ? `;${mobileWidthCss}` : '') +
        '}' +
        `.${cls}__item{display:flex;align-items:center;${itemLayout}--aqx-icon-size:${mIcon}px;gap:${mGap}px}` +
        `.${cls}__text{font-size:${mText}px;color:var(--aqx-scheme-text);` +
        `font-family:var(--aqx-theme-heading-font, var(--aqx-theme-font));` +
        `font-weight:var(--aqx-theme-fw-semibold, 600);line-height:var(--aqx-theme-lh-snug, 1.25)}` +
        // Desktop override via `@container aqx-page` ONLY (always present via PageRenderer) — a paired
        // @media(min-width) would fire off the builder's wide editor window inside a narrow mobile preview.
        `@container aqx-page (min-width:750px){${desktopRule}}` +
        visibilityCss(cls, bs.visibility);
    const style = marginStyle(bs, 15, 15);
    return (_jsxs(_Fragment, { children: [_jsx(MaterialSymbolsFont, {}), _jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("ul", { className: cls, style: style, children: items.map((it) => (_jsxs("li", { className: `${cls}__item`, children: [_jsx("span", { style: { flex: '0 0 auto', lineHeight: 0 }, children: _jsx(MaterialIcon, { name: it.icon, filled: it.filled, image: it.image, size: dIcon, color: iconColor }) }), _jsx("span", { className: `${cls}__text`, children: it.heading })] }, it.n))) })] }));
};
export const iconWithTextBlock = {
    type: 'icon-with-text',
    schema: iconWithTextBlockSchema,
    Component: IconWithTextBlock,
    meta: { label: 'Icons with text' },
};
//# sourceMappingURL=index.js.map